import utils from './drawUtils'
import canvasActions from './canvasActions'
import challenge from '../challenge/challenge'

// Layout constants for the nodes
const bottomMargin = 5
const lineSpacing = 3
const widthMargin = 5

// main rectangle layout
const rectCornerRadius = 2
const rectStroke = '#000000'
const rectStrokeWidth = 1
const rectShadow = '#000000'
const rectShadowSize = 10

const rectSelectedStrokeWidth = 4 // used when selected for group move

// edit handle layout
const handleRadius = 10
const handleStroke = '#000000'
const handleStrokeWidth = 1

// header layout
const pinIconSize = 20
const logoSize = 20
const headerMargin = 5
const nodeLabelWidth = 30
const pinCountWidth = 30
const pinLabelWidth = 45

// computed from constants above, do not change
const headerHeight = 2 * headerMargin + Math.max(pinIconSize, logoSize)
const headerMinWidth = headerMargin + logoSize + nodeLabelWidth + pinIconSize + pinCountWidth + 60

export default class {
  constructor (id, pos, size, fill, text, font, fontColor, dotColor, pins, pinnedByMe, pinned) {
    this.id = id
    this.pos = pos
    this.size = size
    this.fill = fill
    this.text = text
    this.font = font
    this.fontColor = fontColor
    this.dotColor = dotColor
    this.pins = pins
    this.pinnedByMe = pinnedByMe
    this.pinned = pinned
  }

  getFitSize (ctx, text, font, width, height) {
    const minTextWidth = utils.getTextMinWidth(ctx, text, font)
    const minWidth = Math.max(minTextWidth + 2 * widthMargin, headerMinWidth)
    if (width < minWidth) {
      width = minWidth
    }

    const minTextHeight = utils.getTextMinHeight(ctx, text, font,
      width - 2 * widthMargin, // allowed width for text
      lineSpacing
    )
    const minHeight = minTextHeight + headerHeight + bottomMargin
    if (height < minHeight) {
      height = minHeight
    }

    return {
      width: width,
      height: height
    }
  }

  draw (ctx, state) {
    const logoImg = document.getElementById('canvas_logo')

    // Draw rectangle with topleft at this.pos
    const nodeRect = utils.drawRectWithShadow(ctx,
      this.pos[0], this.pos[1], // topleft position
      this.size[0], this.size[1], // size
      rectCornerRadius, // corner radius
      this.fill, // fill color
      rectStroke, rectStrokeWidth, // stroke
      rectShadow, rectShadowSize // shadow
    )

    // Draw text in center of rectangle
    utils.drawTextMultiline(ctx,
      this.text,
      this.pos[0] + this.size[0] / 2, this.pos[1] + headerHeight, // center x, top y
      this.font, this.fontColor, // font & color
      this.size[0] - widthMargin * 2, lineSpacing, // max width and line spacing
      nodeRect // clip shape
    )

    // Draw node number & logo in the top left corner
    if (logoImg) {
      ctx.drawImage(
        logoImg, // image
        this.pos[0] + headerMargin, this.pos[1] + headerHeight / 2 - logoSize / 2, // topleft position
        logoSize, logoSize // size
      )
    }
    utils.drawText(ctx,
      `#${this.id}`,
      this.pos[0] + headerMargin + logoSize + 10 + nodeLabelWidth / 2, this.pos[1] + headerHeight / 2, // center position
      'bold 30px arial', 'black', // font & color
      nodeRect // clip shape
    )

    // Draw dot and count in top right corner
    utils.drawText(ctx,
      'Dots',
      this.pos[0] + this.size[0] - (pinIconSize + pinCountWidth + pinLabelWidth) + pinLabelWidth / 2, // center x
      this.pos[1] + headerHeight / 2 - 1, // center y, need to subtract 1 for this to align, I don't know why
      '14px arial', 'black', // font & color
      nodeRect // clip shape
    )

    utils.drawCircle(ctx,
      this.pos[0] + this.size[0] - (pinIconSize + pinCountWidth) + pinIconSize / 2, // center x
      this.pos[1] + headerHeight / 2, // center y
      pinIconSize / 2, // radius
      this.pinnedByMe ? challenge.state.currentlyViewedChallenge.dotColor : '#00000000', // fill color
      challenge.state.currentlyViewedChallenge.dotColor, 2 // stroke color & width
    )
    utils.drawText(ctx,
      this.pins.toString(),
      this.pos[0] + this.size[0] - pinCountWidth / 2, this.pos[1] + headerHeight / 2, // center position
      '14px arial', 'black', // font & color
      nodeRect // clip shape
    )

    if (state.canvasAction === canvasActions.SelectNodes) {
      // Select Mode - draw thicker boarder if selected
      const actionData = state.canvasActionData

      if ((actionData.selectedNodeIDs !== undefined) && actionData.selectedNodeIDs.includes(this.id)) {
        utils.drawRect(ctx,
          this.pos[0], this.pos[1], // topleft position
          this.size[0], this.size[1], // size
          rectCornerRadius, // corner radius
          '#00000000', // fill (transparent)
          rectStroke, rectSelectedStrokeWidth // stroke
        )
      }
    } else if (state.canvasAction === canvasActions.EditNode) {
      // Edit Mode - draw handles
      utils.drawCircle(ctx,
        this.pos[0], this.pos[1], // center position
        handleRadius, this.fill, // fill & radius
        handleStroke, handleStrokeWidth // stroke
      )
      utils.drawCircle(ctx,
        this.pos[0] + this.size[0], this.pos[1] + this.size[1], // center position
        handleRadius, this.fill, // fill & radius
        handleStroke, handleStrokeWidth // stroke
      )
    } else if (state.canvasAction === canvasActions.AddLink) {
      const actionData = state.canvasActionData

      if ((actionData.A !== undefined) && (actionData.A.id === this.id)) {
        utils.drawRect(ctx,
          this.pos[0], this.pos[1], // topleft position
          this.size[0], this.size[1], // size
          rectCornerRadius, // corner radius
          '#00000000', // fill (transparent)
          rectStroke, rectSelectedStrokeWidth // stroke
        )
      }
    }
  }

  // checks if a point is in the node's rectangle
  isIn (x, y) {
    const inRect = (this.pos[0] <= x) && (x <= (this.pos[0] + this.size[0])) &&
                   (this.pos[1] <= y) && (y <= (this.pos[1] + this.size[1]))

    return inRect
  }

  // checks if a point is in the node's pin box (top right corner)
  isInPinBox (x, y) {
    const pinBoxMinX = this.pos[0] + this.size[0] - (pinIconSize + pinCountWidth + pinLabelWidth)
    const pinBoxMinY = this.pos[1]
    const pinBoxMaxX = this.pos[0] + this.size[0]
    const pinBoxMaxY = this.pos[1] + headerHeight

    return (pinBoxMinX <= x) && (x <= pinBoxMaxX) &&
           (pinBoxMinY <= y) && (y <= pinBoxMaxY)
  }

  // checks if a point is in the node's position (topleft) edit handle
  isInPosHandle (x, y) {
    const distSq = (this.pos[0] - x) * (this.pos[0] - x) + (this.pos[1] - y) * (this.pos[1] - y)
    return distSq < handleRadius * handleRadius
  }

  // checks if a point is in the node's size (bottomright) edit handle
  isInSizeHandle (x, y) {
    const Hx = this.pos[0] + this.size[0]
    const Hy = this.pos[1] + this.size[1]
    const distSq = (Hx - x) * (Hx - x) + (Hy - y) * (Hy - y)
    return distSq < handleRadius * handleRadius
  }
}
